// const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const API_BASE_URL = '/api';

export const fetchCustomers = async () => {
  try {
    // Constructs the full URL for the /client-names endpoint.
    console.log(API_BASE_URL)
    const response = await fetch(`${API_BASE_URL}/bigquery/client-names`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Expecting data in the format: [{"ClientCode": "C1", "ClientName": "ADP"}, ...]
    return await response.json(); 

  } catch (e) {
    console.error("Failed to fetch client data:", e);
    // In case of an error, we throw it so the component using this service can handle it.
    throw e;
  }
};


/**
 * Streams a chat response from /chat/ via Server-Sent Events.
 *
 * The backend emits events in this order:
 *   sources → array of retrieved chunks (once, after vector search)
 *   token   → {content: "..."} (many, one per LLM streaming token)
 *   log_id  → string (once, after the last token)
 *   done    → {} (once, terminal)
 *   error   → {message: "..."} (only on failure)
 *
 * Pass per-event callbacks via the `handlers` arg. Pass an AbortSignal to cancel.
 */
export const streamChatMessage = async (
  clientCode,
  query,
  chatHistory,
  countryCode,
  contactId,
  conversationId,
  languageCode = 'en',
  lob = '',
  { onSources, onToken, onLogId, onDone, onError, signal } = {}
) => {
  try {
    const response = await fetch(`${API_BASE_URL}/chat/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify({
        clientCode,
        query,
        chatHistory,
        language_code: languageCode,
        country_code: countryCode,
        contact_id: contactId,
        conversation_id: conversationId,
        lob: lob || null,
      }),
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE: events are separated by a blank line (\n\n)
      let sepIdx;
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const eventBlock = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);

        let eventType = 'message';
        let dataStr = '';
        for (const line of eventBlock.split('\n')) {
          if (line.startsWith('event:')) eventType = line.slice(6).trim();
          else if (line.startsWith('data:')) dataStr = line.slice(5).trim();
        }
        if (!dataStr) continue;

        let data;
        try {
          data = JSON.parse(dataStr);
        } catch {
          console.error('Failed to parse SSE data:', dataStr);
          continue;
        }

        if (eventType === 'sources') onSources?.(data);
        else if (eventType === 'token') onToken?.(data.content);
        else if (eventType === 'log_id') onLogId?.(data);
        else if (eventType === 'done') onDone?.();
        else if (eventType === 'error') onError?.(new Error(data.message || 'Stream error'));
      }
    }
  } catch (e) {
    if (e.name === 'AbortError') return;
    console.error('Failed to stream chat message:', e);
    onError?.(e);
  }
};



/**
 * **NEW FUNCTION**
 * Posts the data ingestion form to the backend.
 * @param {object} formData The data from the form.
 * @returns {Promise<object>} The backend's response.
 */
export const transferPalantirData = async (formData) => {
    const payload = {
        dataset_rid: formData.datasetRid,
        table_name: formData.tableName,
        row_limit: parseInt(formData.rowCount, 10) || 0,
        branch_name: "master",
        format_type: "CSV",
        write_disposition: "WRITE_TRUNCATE"
    };

    try {
        const response = await fetch(`${API_BASE_URL}/palantir-bq/transfer`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'accept': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'An unknown error occurred' }));
            throw new Error(errorData.detail);
        }

        return await response.json();

    } catch (e) {
        console.error("Failed to submit data transfer request:", e);
        throw e;
    }
};


export const copyPalantirDataset = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/palantir-bq/copy-dataset`, {
            method: 'GET',
            headers: {
                'accept': 'application/json'
            }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'An unknown error occurred' }));
            throw new Error(errorData.detail);
        }

        return await response.json();

    } catch (e) {
        console.error("Failed to submit copy dataset request:", e);
        throw e;
    }
};


/**
 * **NEW FUNCTION**
 * Triggers the document ingestion pipeline for a specific client.
 * @param {string} clientName The name of the client.
 * @returns {Promise<object>} The backend's response.
 */
export const ingestClientDocs = async (clientName) => {
    const payload = { clientName };
    try {
        const response = await fetch(`${API_BASE_URL}/ingestion/ingest-client-docs`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'accept': 'application/json'
            },
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'An unknown error occurred' }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("Failed to submit ingest client docs request:", e);
        throw e;
    }
};


/**
 * **NEW FUNCTION**
 * Gets a signed URL for a specific file from the backend.
 * @param {string} filename The name of the file.
 * @param {string} sourceType The type of the source (e.g., 'calendars').
 * @returns {Promise<object>} The backend's response containing the signed URL.
 */
export const generateSignedUrl = async (filename, sourceType) => {
    const payload = { filename, source_type: sourceType };
    try {
        const response = await fetch(`${API_BASE_URL}/chat/generateSignedUrl`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: `HTTP error! status: ${response.status}` }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("Failed to generate signed URL:", e);
        throw e;
    }
};


/**
 * **NEW FUNCTION**
 * Triggers the deletion of a folder's contents.
 * @param {string} folderName The name of the folder to delete.
 * @returns {Promise<object>} The backend's response.
 */
export const deleteFolderInfo = async (folderName) => {
    const payload = { folder_name: folderName };
    try {
        const response = await fetch(`${API_BASE_URL}/ingestion/deleteFolderInfo`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'accept': 'application/json'
            },
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'An unknown error occurred' }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("Failed to submit delete folder request:", e);
        throw e;
    }
};


/**
 * **NEW FUNCTION**
 * Triggers the data transfer process that creates files.
 * @returns {Promise<object>} The backend's response.
 */
export const transferData = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/ingestion/transfer-data`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'accept': 'application/json'
            },
            body: JSON.stringify({}) // Send empty body as the endpoint doesn't require a payload
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'An unknown error occurred' }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("Failed to submit transfer data request:", e);
        throw e;
    }
};


export const submitFeedback = async (payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/feedback/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error: ${response.status}`);
    }

    return await response.json();
  } catch (e) {
    console.error("Failed to submit feedback:", e);
    throw e;
  }
};
